// The Sapper on its card (v2-the-deep.md §11.1, §18.1; mockup round 088
// card 12 b; R2635, R2651, R2654).
//
// LAY is the action press of the order row: it lays the LAST kind again,
// and the press names that kind, so the thumb knows what drops before it
// lands (card 12 b). The client keeps the last kind it laid for each hull
// (`MapCardCtx.lastLay`); with none, or with none of that kind aboard, LAY
// takes the first kind of the stow in kind order. The press always sends
// its kind, so the press and the lay name one kind. COLLECT stands in ⋯.
//
// The card states the stow as the fourth vital against the cargo of the
// hull, the two timers of the wire as facts (the wait to the next LAY and
// the loud lay), and a STOW section in the body: one 44 px row for each
// kind aboard with its own LAY press, lit for the last kind.
//
// The kind LAY lays now is the active kind of the Sapper. The card shows
// it as the Lancer shows its active round: a compact pill group of the
// kinds aboard, with the key cap on the next kind (R2710). A pill press
// and the key `n` set the kind and lay nothing; `f` lays it and `s`
// collects, as the presses of the order row do.
//
// The wire sends the stow and the two timers on an own Sapper alone, and
// only while `sapper` is latched (R2654). The control reads the flag too.
//
// Everything here is pure, so a test reads it with no overlay.

import { ShipClass } from '../../gen/proto/aquaphobia';
import type { SeaObjectSpec, SeaObjectStock, ShipSpec } from '../../gen/proto/aquaphobia';
import { card as S } from '../../gen/strings';
import { type GameState, dur } from '../../state';
import { esc } from '../esc';
import { ratioFig } from '../format';
import { rowIcon } from '../overlay/buildrow';
import { pillGroup } from '../choice/choice';
import { cargoSum } from '../rounds';
import { buttonHtml, type Action, type CardEnv, type Fact, type Vital } from './card';

/** The act of a LAY press: the hull in `id`, the kind in `n`. */
const LAY_ACT = 'lay';

/** The act of a pill of the lay pick: the id is "fleet:kind", and the
 *  press sets the kind LAY lays and lays nothing (R2710). */
export const LAY_PICK_ACT = 'laypick';

/** What the Sapper parts read of the state. */
export type SapperState = Pick<GameState, 'hasFeatureFlag' | 'seaObject' | 'mapToWorldSec'>;

/** What the Sapper parts read of an own hull. */
export interface SapperFleet {
  id: number;
  cls: ShipClass;
  stow?: readonly SeaObjectStock[];
  layReadySec?: number;
  loudLeftSec?: number;
}

/** Is this an own Sapper whose card shows the Sapper parts? */
export function isSapperCard(s: Pick<GameState, 'hasFeatureFlag'>, cls: ShipClass): boolean {
  return cls === ShipClass.SHIP_CLASS_SAPPER && s.hasFeatureFlag('sapper');
}

/** The items aboard, of every kind. */
export function stowCount(stow: readonly SeaObjectStock[] | undefined): number {
  let n = 0;
  for (const r of stow ?? []) n += r.count;
  return n;
}

/** The cargo a stow fills: the count of each kind × the cargo of one item
 *  of the kind (`SeaObjectSpec.cargo`). */
export function stowCargo(seaObject: (k: number) => SeaObjectSpec | undefined,
  stow: readonly SeaObjectStock[] | undefined): number {
  return cargoSum(stow ?? [], (k) => seaObject(k)?.cargo ?? 0);
}

/** The rows of a stow with an item, in kind order. */
function stowRows(stow: readonly SeaObjectStock[] | undefined): SeaObjectStock[] {
  return (stow ?? []).filter((r) => r.count > 0).sort((a, b) => a.kind - b.kind);
}

/**
 * The kind LAY lays now (R2635): the last kind while the stow holds one,
 * else the first kind of the stow in kind order; 0 for an empty stow.
 */
export function layKind(stow: readonly SeaObjectStock[] | undefined, last: number): number {
  const rows = stowRows(stow);
  if (last !== 0 && rows.some((r) => r.kind === last)) return last;
  return rows[0]?.kind ?? 0;
}

/**
 * The kind the key of the next kind switches LAY to (R2710): the kind
 * after the kind LAY lays now, among the kinds aboard in kind order, which
 * wraps. 0 while fewer than two kinds are aboard, as a Lancer of one round
 * kind has no next round.
 */
export function nextLayKind(stow: readonly SeaObjectStock[] | undefined, last: number): number {
  const kinds = stowRows(stow).map((r) => r.kind);
  if (kinds.length < 2) return 0;
  return kinds[(kinds.indexOf(layKind(stow, last)) + 1) % kinds.length];
}

/**
 * The kind the key of LAY lays now (R2710), with the gates of the press:
 * a kind aboard and no lay wait. 0 when the key lays nothing.
 */
export function layKeyKind(f: SapperFleet, last: number): number {
  if ((f.layReadySec ?? 0) > 0) return 0;
  return layKind(f.stow, last);
}

/**
 * The lay pick of the card body (R2710), the twin of the round pick of a
 * Lancer (`roundPickHtml`): the cap, then one compact pill for each kind
 * aboard with its count. The pill of the kind LAY lays is lit, and the
 * pill the key switches to carries the key cap `key`. Undefined while
 * fewer than two kinds are aboard.
 */
export function layPickHtml(s: Pick<SapperState, 'seaObject'>, f: SapperFleet, last: number,
  key?: string): string | undefined {
  const rows = stowRows(f.stow);
  if (rows.length < 2) return undefined;
  const now = layKind(f.stow, last);
  const next = key ? nextLayKind(f.stow, last) : 0;
  const idOf = (k: number) => `${f.id}:${k}`;
  const list = rows.map((r) => ({
    id: idOf(r.kind), label: (s.seaObject(r.kind)?.name ?? '?').toUpperCase(), n: r.count,
    title: S.lay_pick_title, key: r.kind === next ? key : undefined,
  }));
  return `<div class="ck-sect"><span class="ck-cap">${S.lay_pick}</span>`
    + pillGroup(list, idOf(now), LAY_PICK_ACT, { label: S.lay_pick, size: 'compact' })
    + '</div>';
}

/** The map seconds to the next LAY as the card prints them, in whole
 *  world seconds (R591); 0 when LAY is ready. */
function layWait(s: SapperState, f: SapperFleet): number {
  const sec = f.layReadySec ?? 0;
  return sec > 0 ? s.mapToWorldSec(sec) : 0;
}

/**
 * The LAY press of the order row (card 12 b): the word with the last kind,
 * the wait while the cadence runs, and off with an empty stow. The press
 * sends its kind in `n`. `key` is the cap of its key (R2710).
 */
export function layAction(s: SapperState, f: SapperFleet, last: number, face: 'dark' | 'quiet',
  key?: string): Action {
  const kind = layKind(f.stow, last);
  const name = kind ? s.seaObject(kind)?.name ?? '' : '';
  const wait = layWait(s, f);
  return {
    act: LAY_ACT, id: f.id, n: kind, face, key,
    label: wait > 0 ? S.o_lay_wait({ left: dur(wait) }) : name ? S.o_lay_kind({ kind: name.toUpperCase() }) : S.o_lay,
    off: wait > 0 || kind === 0,
    title: kind ? S.o_lay_title({ kind: name }) : S.o_lay_empty_title,
  };
}

/** COLLECT of ⋯ (R2651): the server starts it at rest and refuses a hull
 *  that moves. `key` is the cap of its key (R2710). */
export function collectAction(f: SapperFleet, face: 'dark' | 'quiet', key?: string): Action {
  return { act: 'collect', id: f.id, face, key, label: S.o_collect, title: S.o_collect_title };
}

/** The fourth vital of a Sapper: the stow against the cargo of the hull. */
export function stowVital(s: SapperState, f: SapperFleet, spec: ShipSpec | undefined): Vital {
  const used = stowCargo((k) => s.seaObject(k), f.stow);
  const cap = spec?.cargo ?? 0;
  return {
    label: S.v_stow, ink: 'load', fig: cap > 0 ? ratioFig(used, cap) : String(used),
    frac: cap > 0 ? Math.min(1, used / cap) : 0,
  };
}

/** The two timers of the wire as facts: the wait to the next LAY, and the
 *  loud lay while it runs. */
export function sapperFacts(s: SapperState, f: SapperFleet): Fact[] {
  const wait = layWait(s, f);
  const facts: Fact[] = [{ key: S.f_lay, val: esc(wait > 0 ? dur(wait) : S.lay_ready) }];
  const loud = f.loudLeftSec ?? 0;
  if (loud > 0) {
    facts.push({ key: S.f_loud, val: esc(S.loud_left({ left: dur(s.mapToWorldSec(loud)) })), title: S.loud_title });
  }
  return facts;
}

/**
 * The STOW section of the card body (card 12): the head with the cargo
 * against the room, then one row for each kind aboard: the name and the
 * count, the family and the cargo of one item, and LAY of that kind, lit
 * for the kind LAY of the order row lays. An empty stow states how the
 * stow fills.
 */
export function stowSectionHtml(s: SapperState, f: SapperFleet, spec: ShipSpec | undefined,
  last: number, env: CardEnv): string {
  const used = stowCargo((k) => s.seaObject(k), f.stow);
  const head = `<span class="ck-cap">${esc(S.stow_head({ used, cap: spec?.cargo ?? 0 }))}</span>`;
  const rows = stowRows(f.stow);
  if (rows.length === 0) return `<div class="ck-sect ck-stow">${head}<p class="ck-stown">${esc(S.stow_empty)}</p></div>`;
  const now = layKind(f.stow, last);
  const wait = layWait(s, f);
  const list = rows.map((r) => {
    const k = s.seaObject(r.kind);
    const name = k?.name ?? '?';
    const sub = k?.seaMine ? S.stow_row_sub_mine({ cargo: k.cargo }) : S.stow_row_sub_deployable({ cargo: k?.cargo ?? 0 });
    const press = buttonHtml({
      act: LAY_ACT, id: f.id, n: r.kind, face: 'quiet', on: r.kind === now,
      label: wait > 0 ? S.o_lay_wait({ left: dur(wait) }) : S.stow_lay, off: wait > 0,
      title: S.o_lay_title({ kind: name }),
    }, env);
    // The word LAST names the kind the client laid last, as card 12 does.
    return `<div class="ck-row ck-sr${r.kind === now ? ' on' : ''}">${rowIcon(k?.seaMine ? 'mine' : 'deployable')}`
      + `<span class="ck-rn"><b>${esc(S.stow_row({ name, n: r.count }))}</b>`
      + `<i>${esc(r.kind === last ? `${S.stow_last} · ${sub}` : sub)}</i></span>${press}</div>`;
  }).join('');
  return `<div class="ck-sect ck-stow">${head}<div class="ck-list">${list}</div></div>`;
}
